import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Home, Search } from "lucide-react";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useLanguage } from "@/contexts/LanguageContext";

const notFoundTranslations = {
  en: {
    title: "Page Not Found",
    description: "The page you are looking for does not exist or has been moved.",
    homeButton: "Go Home",
    blogButton: "Blog",
    seoTitle: "404 - Page Not Found | Hair Art Clinics",
    seoDescription: "The page you are looking for does not exist. Return to our homepage to explore our hair transplant services, treatments, and expert consultation."
  },
  tr: {
    title: "Sayfa Bulunamadı",
    description: "Aradığınız sayfa mevcut değil veya taşınmış olabilir.",
    homeButton: "Ana Sayfaya Dön",
    blogButton: "Blog",
    seoTitle: "404 - Sayfa Bulunamadı | Hair Art Clinics",
    seoDescription: "Aradığınız sayfa mevcut değil. Saç ekimi hizmetlerimizi, tedavilerimizi ve uzman danışmanlığımızı keşfetmek için ana sayfamıza dönün."
  },
  fr: {
    title: "Page Non Trouvée",
    description: "La page que vous recherchez n'existe pas ou a été déplacée.",
    homeButton: "Accueil",
    blogButton: "Blog",
    seoTitle: "404 - Page Non Trouvée | Hair Art Clinics",
    seoDescription: "La page que vous recherchez n'existe pas. Retournez à notre page d'accueil pour découvrir nos services de greffe de cheveux."
  },
  nl: {
    title: "Pagina Niet Gevonden",
    description: "De pagina die u zoekt bestaat niet of is verplaatst.",
    homeButton: "Naar Home",
    blogButton: "Blog",
    seoTitle: "404 - Pagina Niet Gevonden | Hair Art Clinics",
    seoDescription: "De pagina die u zoekt bestaat niet. Keer terug naar onze homepage om onze haartransplantatie diensten te ontdekken."
  },
  ar: {
    title: "الصفحة غير موجودة",
    description: "الصفحة التي تبحث عنها غير موجودة أو تم نقلها.",
    homeButton: "الرئيسية",
    blogButton: "المدونة",
    seoTitle: "404 - الصفحة غير موجودة | Hair Art Clinics",
    seoDescription: "الصفحة التي تبحث عنها غير موجودة. عد إلى صفحتنا الرئيسية لاستكشاف خدمات زراعة الشعر لدينا."
  }
};

const NotFound = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const t = notFoundTranslations[language] || notFoundTranslations.en;

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-background to-muted">
      <SEOHead
        title={t.seoTitle}
        description={t.seoDescription}
        noHreflang
      />
      <div className="text-center max-w-md px-4">
        <div className="mb-8">
          <h1 className="text-8xl font-bold text-primary mb-4">404</h1>
          <h2 className="text-2xl font-semibold mb-2">{t.title}</h2>
          <p className="text-muted-foreground">
            {t.description}
          </p>
        </div>
        <div className="flex gap-4 justify-center">
          <Button asChild size="lg">
            <LocalizedLink to="/">
              <Home className="mr-2 h-4 w-4" />
              {t.homeButton}
            </LocalizedLink>
          </Button>
          <Button asChild variant="outline" size="lg">
            <LocalizedLink to="/blog">
              <Search className="mr-2 h-4 w-4" />
              {t.blogButton}
            </LocalizedLink>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
